import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Check, X, CalendarDays, PartyPopper, Trash2 } from 'lucide-react';

const statusStyle = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const LeaveTypeColor = {
  paid: 'text-sky-600 bg-sky-50',
  sick: 'text-rose-600 bg-rose-50',
};

export default function Leaves() {
  const { user } = useAuth();
  const isAdminHR = ['admin', 'hr'].includes(user?.role);
  const canDecide = ['admin', 'hr', 'reporting_manager'].includes(user?.role);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [balance, setBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [open, setOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: 'paid', start_date: '', end_date: '', reason: '' });
  const [hForm, setHForm] = useState({ name: '', date: '', description: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [m, a, b, h] = await Promise.all([
      api.get('/leaves?scope=me'),
      canDecide ? api.get('/leaves?scope=all') : Promise.resolve({ data: [] }),
      api.get('/leave-balance/me'),
      api.get('/holidays'),
    ]);
    setMine(m.data); setAll(a.data); setBalance(b.data); setHolidays(h.data);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const apply = async () => {
    if (!form.start_date || !form.end_date || !form.reason) return toast.error('Please fill all fields');
    setBusy(true);
    try { await api.post('/leaves', form); toast.success('Leave request submitted'); setOpen(false);
          setForm({ leave_type: 'paid', start_date: '', end_date: '', reason: '' }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decide = async (id, action) => {
    try { await api.post(`/leaves/${id}/decision?action=${action}`, {}); toast.success(`Leave ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createHoliday = async () => {
    if (!hForm.name || !hForm.date) return toast.error('Name and date required');
    try { await api.post('/holidays', hForm); toast.success('Holiday added'); setHolidayOpen(false); setHForm({ name: '', date: '', description: '' }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteHoliday = async (id) => {
    try { await api.delete(`/holidays/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renderRow = (lv, showActions) => (
    <div key={lv.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`leave-${lv.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-slate-900 capitalize">{lv.leave_type} leave</span>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusStyle[lv.status]}`}>{lv.status}</span>
        </div>
        {showActions && <div className="text-xs text-slate-500 mt-1">{lv.user_name} · {lv.user_email}</div>}
        <div className="text-sm text-slate-600 mt-2">
          <span className="font-medium">{lv.start_date}</span> → <span className="font-medium">{lv.end_date}</span>
        </div>
        <div className="text-sm text-slate-500 mt-1 italic">"{lv.reason}"</div>
      </div>
      {showActions && lv.status === 'pending' && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => decide(lv.id, 'approve')} data-testid={`approve-${lv.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => decide(lv.id, 'reject')} data-testid={`reject-${lv.id}`} className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Time off</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Leave management</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="apply-leave-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
              <Plus className="w-4 h-4 mr-2" /> Apply for leave
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="apply-leave-dialog" className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Apply for leave</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Leave type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger data-testid="leave-type-select" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid ({balance?.remaining?.paid ?? 0} left)</SelectItem>
                    <SelectItem value="sick">Sick ({balance?.remaining?.sick ?? 0} left)</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Start date</Label>
                  <Input type="date" data-testid="leave-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">End date</Label>
                  <Input type="date" data-testid="leave-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Reason</Label>
                <Textarea data-testid="leave-reason" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-xl mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={apply} disabled={busy} data-testid="submit-leave" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                {busy ? 'Submitting…' : 'Submit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave balance widget */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leave-balance">
          {['paid', 'sick'].map(t => (
            <div key={t} className={`rounded-2xl border border-slate-200 p-5 bg-white`} data-testid={`balance-${t}`}>
              <div className={`text-xs font-bold uppercase tracking-[0.2em] inline-block px-2 py-1 rounded-full ${LeaveTypeColor[t]}`}>{t}</div>
              <div className="font-heading text-3xl font-bold mt-2 text-slate-900">{balance.remaining[t]}<span className="text-base text-slate-400 font-normal"> / {balance.entitlement[t]}</span></div>
              <div className="text-xs text-slate-500 mt-1">{balance.used[t]} used this year</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="mine">
        <TabsList className="rounded-full bg-slate-100 p-1 h-auto">
          <TabsTrigger value="mine" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-my-leaves">My requests</TabsTrigger>
          {canDecide && <TabsTrigger value="all" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-all-leaves">Pending approvals</TabsTrigger>}
          <TabsTrigger value="holidays" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-holidays">Holiday calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="mine" className="mt-5 space-y-3">
          {mine.map(lv => renderRow(lv, false))}
          {mine.length === 0 && (
            <div className="text-center py-16 text-slate-400 flex flex-col items-center gap-3">
              <CalendarDays className="w-10 h-10 text-slate-300" />
              <div>No leave requests yet.</div>
            </div>
          )}
        </TabsContent>
        {canDecide && (
          <TabsContent value="all" className="mt-5 space-y-3">
            {all.map(lv => renderRow(lv, true))}
            {all.length === 0 && <div className="text-center py-16 text-slate-400">No team leave requests.</div>}
          </TabsContent>
        )}
        <TabsContent value="holidays" className="mt-5">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-500">{holidays.length} holidays this year</div>
            {isAdminHR && (
              <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-holiday-button" className="rounded-full">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add holiday
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle className="font-heading">Add holiday</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div><Label className="text-xs font-semibold text-slate-600">Name</Label><Input data-testid="holiday-name" value={hForm.name} onChange={(e) => setHForm({...hForm, name: e.target.value})} className="rounded-xl mt-1" /></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Date</Label><Input data-testid="holiday-date" type="date" value={hForm.date} onChange={(e) => setHForm({...hForm, date: e.target.value})} className="rounded-xl mt-1" /></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Description</Label><Input data-testid="holiday-desc" value={hForm.description} onChange={(e) => setHForm({...hForm, description: e.target.value})} className="rounded-xl mt-1" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setHolidayOpen(false)} className="rounded-full">Cancel</Button>
                    <Button onClick={createHoliday} data-testid="submit-holiday" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">Add</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {holidays.map(h => (
              <div key={h.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`holiday-${h.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <PartyPopper className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{h.name}</div>
                    <div className="text-xs text-slate-500">{new Date(h.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  </div>
                </div>
                {isAdminHR && (
                  <button onClick={() => deleteHoliday(h.id)} data-testid={`delete-holiday-${h.id}`} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
